import {
    Autocomplete,
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";

// --- Interfaces and Constants ---
interface APIGatewayFormProps {
    isEdit: boolean;
    isAddToExisting?: boolean;
}

interface ApiBackend {
  id: string;
  name: string;
}

const addToExistingValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required("API Gateway Name is required")
        .matches(/^[a-z0-9-]*$/, "Must contain only lowercase letters, numbers, and hyphens"),
    apis: Yup.array().min(1, "At least one API must be selected").required("APIs Backend are required"),
});

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required("API Gateway Name is required")
        .matches(/^[a-z0-9-]*$/, "Must contain only lowercase letters, numbers, and hyphens"),
    apiType: Yup.string().required("API Type is required"),
    endpointType: Yup.string().required("Endpoint Type is required"),
    authorizerType: Yup.string().required("Authorizer Type is required"),
    userPoolName: Yup.string().when("authorizerType", {
        is: "Cognito",
        then: (schema) => schema
            .required("User Pool Name is required")
            .matches(/^[a-z0-9-]*$/, "Must contain only lowercase letters, numbers, and hyphens"),
        otherwise: (schema) => schema.notRequired(),
    }),
    region: Yup.string().required("Region is required"),
    consumerGroup: Yup.string().required("Consumer Group is required"),
    wafEnabled: Yup.boolean(),
    tokenHeaderSource: Yup.string().required("Token Header Source is required"),
    apis: Yup.array().min(1, "At least one API must be selected").required("APIs Backend are required"),
    categories: Yup.string().required("Category is required"),
    addBotControl: Yup.boolean(),
    costCenterValue: Yup.string()
        .required("This field is required")
        .strict()
        .lowercase("This field must be in lowercase")
        .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
        .max(20, "This field must not exceed 20 characters"),
    costLabelPairs: Yup.array()
        .of(
            Yup.object({
                cost: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"),
                label: Yup.string()
                    .required("This field is required")
                    .strict()
                    .lowercase("This field must be in lowercase")
                    .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
                    .max(20, "This field must not exceed 20 characters"),
            })
        )
});

const apiTypeList = ["HTTP", "REST", "Websocket"];
const endpointTypeList = ["Regional", "Edge Optimised", "Private"];
const authorizerTypeList = ["Cognito", "Lambda"];
const awsRegionsList = [
    "us-east-1", "us-east-2", "us-west-1", "us-west-2",
    "af-south-1", "ap-east-1", "ap-south-1", "ap-northeast-1",
    "ap-northeast-2", "ap-southeast-1", "ap-southeast-2",
    "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2",
    "eu-south-1", "eu-west-3", "eu-north-1", "me-south-1", "sa-east-1"
];
const categoryList = ["Testing", "Sales", "Monitization", "Finance"];

// --- Form Content Component ---
const APIGatewayFormContent: React.FC<APIGatewayFormProps> = ({ isEdit, isAddToExisting }) => {
    const navigate = useNavigate();
    const { id, apiGatewayOption } = useParams();
    
    const [isLoading, setIsLoading] = useState(isAddToExisting);
    const [gatewayData, setGatewayData] = useState<any>({});
    const [apiBackendList, setApiBackendList] = useState<ApiBackend[]>([]);
    const [allGateways, setAllGateways] = useState<any[]>([]);
    const [selectedGatewayForUpdate, setSelectedGatewayForUpdate] = useState<any | null>(null);
    const [consumerGroupList, setConsumerGroupList] = useState<any[]>([]); 

    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    useEffect(() => {
        const fetchApiBackends = async () => {
            try {
                const response = await queryGraphql(`
                    query GetAPIBackends {
                        getAPIBackends {
                            id
                            name
                            status
                        }
                    }
                `);
                if (response.getAPIBackends) {
                    const activeBackends = response.getAPIBackends.filter(
                        (backend:any) => backend.status === "active"
                    );
                    setApiBackendList(activeBackends);
                }
            } catch (error) {
                console.error("Error fetching API backends:", error);
            }
        };
        fetchApiBackends();
    }, []);

    useEffect(() => {
        if (!isEdit || !id) return;
        const fetchGatewayData = async () => {
            try {
                const query = `
                    query GetAPIGateway($getApiGatewayId: String!) {
                      getAPIGateway(id: $getApiGatewayId) {
                        id, name, apiGatewayType, apiEndpointType, region, authorizerType, userPoolName, swaggerYaml,
                        tokenHeaderSource, 
                        consumerGroupInfo { 
                            name
                            },
                        apiBackends { id, name },
                        apiGatewayCategory, awsWafEnabled, botEnabled,
                        costLabelPairs { label, cost }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getApiGatewayId: id });
                if (response.getAPIGateway) {
                    setGatewayData(response.getAPIGateway);
                }
            } catch (error) {
                console.error("Error fetching gateway data:", error);
            }
        };
        fetchGatewayData();
    }, [id, isEdit]);

    useEffect(() => {
        if (!isAddToExisting) return;
        setIsLoading(true);
        const fetchAllGateways = async () => {
            try {
                const response = await queryGraphql(`
                    query GetAPIGateways {
                      getAPIGateways {
                        id, name, apiGatewayType, apiEndpointType, region, authorizerType,
                        tokenHeaderSource, 
                        consumerGroupInfo {
                            name
                        },
                        status,
                        apiBackends { id, name },
                        apiGatewayCategory, awsWafEnabled, botEnabled,
                        costLabelPairs { label, cost },
                        swaggerYaml
                      }
                    }
                `);
                if (response.getAPIGateways) {
                    const activeGateways = response.getAPIGateways.filter(
                        (gateway: any) => gateway.status === 'active'
                    );
                    setAllGateways(activeGateways);
                }
            } catch (error) {
                console.error("Error fetching all gateways:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAllGateways();
    }, [isAddToExisting]);

    useEffect(() => {
        const fetchConsumerGroups = async () => {
            try {
                const response = await queryGraphql(`
                    query GetConsumerGroups {
                      getConsumerGroups {
                        id
                        name
                        status
                      }
                    }
                `);
                if (response.getConsumerGroups) {
                    const activeConsumerGroups = response.getConsumerGroups.filter(
                        (group: any) => group.status === 'active'
                    );
                    setConsumerGroupList(activeConsumerGroups);
                }
            } catch (error) {
                console.error("Error fetching consumer groups:", error);
            }
        };
        fetchConsumerGroups();
    }, []);

    return (
        <Container disableGutters maxWidth={false} sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/apis-gateway" onClick={(e) => { e.preventDefault(); navigate('/apis-gateway'); }}>API Gateways</Link>
                    <Typography color="text.primary">{isAddToExisting ? "Add to API Gateway" : (isEdit ? "Edit API Gateway" : "Add New API Gateway")}</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={2}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/apis-gateway")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px"> {isAddToExisting ? "Add to API Gateway" : (isEdit ? "Edit API Gateway" : "Create New API Gateway")}</Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={10}>
                     {isAddToExisting && isLoading && <Typography>Loading active gateways...</Typography>}
                     {isAddToExisting && !isLoading && allGateways.length === 0 && (
                        <Box sx={{ p: 3, backgroundColor: 'white', borderRadius: '8px', textAlign: 'center' }}>
                            <Typography variant="h6">No Active Gateways Found</Typography>
                            <Typography color="text.secondary">There are no active API Gateways available to add APIs to.</Typography>
                        </Box>
                    )}
                    {((!isEdit && !isAddToExisting) || (isEdit && gatewayData.id) || (isAddToExisting && allGateways.length > 0)) && (
                    <Formik
                        initialValues={{
                            id: gatewayData.id || "",
                            name: isAddToExisting ? "" : gatewayData.name || "",
                            apiType: gatewayData.apiGatewayType || "",
                            endpointType: gatewayData.apiEndpointType || "",
                            authorizerType: gatewayData.authorizerType || "",
                            userPoolName: gatewayData.userPoolName || "",
                            region: gatewayData.region || "",
                             consumerGroup: gatewayData.consumerGroupInfo?.name || "",
                            wafEnabled: gatewayData.awsWafEnabled || false,
                            tokenHeaderSource: gatewayData.tokenHeaderSource || "",
                            apis: gatewayData?.apiBackends?.map((api: ApiBackend) => api.name) || [],
                            categories: gatewayData.apiGatewayCategory || "",
                            addBotControl: gatewayData.botEnabled || false,
                            costCenterValue: gatewayData.costLabelPairs?.find((p: any) => p.cost === 'cost-center')?.label || '',
                            costLabelPairs: gatewayData.costLabelPairs?.filter((p: any) => p.cost !== 'cost-center').map((p: any) => ({...p, id: crypto.randomUUID()})) || [],
                        }}
                        validationSchema={isAddToExisting ? addToExistingValidationSchema : validationSchema}
                        enableReinitialize
                        onSubmit={async (values) => {
                            const selectedApiIds = values.apis
                                .map((apiName: string) => apiBackendList.find(api => api.name === apiName)?.id)
                                .filter(Boolean) as string[];

                            const allCostPairs = [
                                { cost: 'cost-center', label: values.costCenterValue },
                                ...values.costLabelPairs.map(({ cost, label }: { cost: string, label: string }) => ({ cost, label }))
                            ];
                            
                            // --- FIX 1: Find the Consumer Group ID from the name stored in `values` ---
                            const consumerGroupId = consumerGroupList.find(group => group.name === values.consumerGroup)?.id;

                            const commonInput = {
                                name: values.name,
                                apiGatewayType: values.apiType,
                                apiEndpointType: values.endpointType,
                                region: values.region,
                                authorizerType: values.authorizerType,
                                userPoolName: values.userPoolName,
                                tokenHeaderSource: values.tokenHeaderSource,
                                consumerGroupInfo: consumerGroupId, // <-- Use the found ID
                                apiBackends: selectedApiIds,
                                apiGatewayCategory: values.categories,
                                awsWafEnabled: values.wafEnabled,
                                botEnabled: values.addBotControl,
                                costLabelPairs: allCostPairs,
                            };
                            
                            try {
                                if (isAddToExisting && selectedGatewayForUpdate) {
                                    // --- FIX 2: Find the Consumer Group ID for the selected gateway ---
                                    const selectedGatewayConsumerGroupId = consumerGroupList.find(group => group.name === selectedGatewayForUpdate.consumerGroup)?.id;

                                    const input = {
                                       id: selectedGatewayForUpdate.id,
                                        name: selectedGatewayForUpdate.name,
                                        apiGatewayType: selectedGatewayForUpdate.apiGatewayType,
                                        apiEndpointType: selectedGatewayForUpdate.apiEndpointType,
                                        region: selectedGatewayForUpdate.region,
                                        authorizerType: selectedGatewayForUpdate.authorizerType,
                                        tokenHeaderSource: selectedGatewayForUpdate.tokenHeaderSource,
                                        consumerGroupInfo: selectedGatewayConsumerGroupId, // <-- Use the found ID
                                        apiGatewayCategory: selectedGatewayForUpdate.apiGatewayCategory,
                                        awsWafEnabled: selectedGatewayForUpdate.awsWafEnabled,
                                        botEnabled: selectedGatewayForUpdate.botEnabled,
                                        apiBackends: selectedApiIds, 
                                        costLabelPairs: selectedGatewayForUpdate.costLabelPairs.map(({ __typename, ...rest }: any) => rest),
                                    };
                                    
                                    await queryGraphql(
                                        `mutation UpdateAPIGateway($input: UpdateAPIGatewayInput!) {
                                            updateAPIGateway(input: $input) { id }
                                        }`, 
                                       { input:{ ...input, status: 'active', }}
                                    );
                                } 
                                else if (isEdit) {
                                    const input = { ...commonInput, id: id };
                                    await queryGraphql(
                                        `mutation UpdateAPIGateway($input: UpdateAPIGatewayInput!) {
                                          updateAPIGateway(input: $input) { id }
                                        }`, 
                                        {input:{ ...input, status: 'pending', }}
                                    );
                                } else {
                                    const input = { ...commonInput, apiGatewayOption: apiGatewayOption };
                                    await queryGraphql(
                                        `mutation CreateAPIGateway($input: CreateAPIGatewayInput!) {
                                          createAPIGateway(input: $input) { id }
                                        }`, 
                                        {input:{ ...input, status: 'pending' }}
                                    );
                                }
                                navigate("/apis-gateway");
                            } catch (error) {
                                console.error("Failed to save API Gateway:", error);
                                alert("Failed to save gateway. Please check the console for errors.");
                            }
                        }}
                    >
                        {({ setFieldValue, errors, touched, values, handleChange, handleBlur, isValid, dirty }) => (
                            <Form id="main-form">
                                <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
                                    {isEdit
                                        ? "Make changes to the gateway details and save"
                                        : "Fill the below details to add an API Gateway"}
                                </Typography>

                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="API Gateway Name" />
                                            {isAddToExisting ? (
                                                <Autocomplete
                                                    options={allGateways.map(gw => gw.name)}
                                                    value={selectedGatewayForUpdate ? selectedGatewayForUpdate.name : null}
                                                    onChange={(_, newValue) => {
                                                        const selectedGw = allGateways.find(gw => gw.name === newValue) || null;
                                                        setSelectedGatewayForUpdate(selectedGw);
                                                        
                                                        if (selectedGw) {
                                                            setFieldValue("name", selectedGw.name || "");
                                                            setFieldValue("id", selectedGw.id || "");
                                                            setFieldValue("apiType", selectedGw.apiGatewayType || "");
                                                            setFieldValue("endpointType", selectedGw.apiEndpointType || "");
                                                            setFieldValue("authorizerType", selectedGw.authorizerType || "");
                                                            setFieldValue("region", selectedGw.region || "");
                                                            setFieldValue("consumerGroup", selectedGw.consumerGroupInfo?.name || "");
                                                            setFieldValue("wafEnabled", selectedGw.awsWafEnabled || false);
                                                            setFieldValue("tokenHeaderSource", selectedGw.tokenHeaderSource || "");
                                                            setFieldValue("categories", selectedGw.apiGatewayCategory || "");
                                                            setFieldValue("addBotControl", selectedGw.botEnabled || false);
                                                            setFieldValue("costCenterValue", selectedGw.costLabelPairs?.find((p: any) => p.cost === 'cost-center')?.label || '');
                                                            setFieldValue("costLabelPairs", selectedGw.costLabelPairs?.filter((p: any) => p.cost !== 'cost-center').map((p: any) => ({...p, id: crypto.randomUUID()})) || []);
                                                            setFieldValue("apis", selectedGw.apiBackends?.map((api: ApiBackend) => api.name) || []);
                                                        } else {
                                                            setFieldValue("name", "");
                                                            setFieldValue("apis", []);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} variant="filled" fullWidth placeholder="Select an API Gateway" hiddenLabel
                                                            sx={{ "& .MuiFilledInput-root": { border: errors.name && touched.name ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                            InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    )}
                                                />
                                            ) : (
                                                <TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter display name of the Gateway" hiddenLabel
                                                    disabled={isEdit}
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.name && touched.name ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            )}
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="API Type" />
                                            <Autocomplete options={apiTypeList} value={values.apiType} disabled={isAddToExisting}
                                                onChange={(_, newValue) => setFieldValue("apiType", newValue || "")}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select the API Type" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.apiType && touched.apiType ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="apiType" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="API Endpoint Type" />
                                            <Autocomplete options={endpointTypeList} value={values.endpointType} disabled={isAddToExisting}
                                                onChange={(_, newValue) => setFieldValue("endpointType", newValue || "")}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select the Endpoint Type" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.endpointType && touched.endpointType ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="endpointType" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Region" />
                                            <Autocomplete options={awsRegionsList} value={values.region} disabled={isAddToExisting}
                                                onChange={(_, newValue) => setFieldValue("region", newValue || "")}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select AWS Region" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.region && touched.region ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="region" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Authorizer Type" />
                                            <Autocomplete options={authorizerTypeList} value={values.authorizerType} disabled={isAddToExisting}
                                                onChange={(_, newValue) => {
                                                    setFieldValue("authorizerType", newValue || "");
                                                    if (newValue !== "Cognito") {
                                                        setFieldValue("userPoolName", "");
                                                    }
                                                }}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select Authorizer Type" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.authorizerType && touched.authorizerType ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="authorizerType" /></div>
                                        </Grid>

                                        {values.authorizerType === 'Cognito' && (
                                            <Grid item xs={12} md={6}>
                                                <CompulsoryFieldsLabel fieldName="User Pool Name" />
                                                <TextField variant="filled" fullWidth name="userPoolName" value={values.userPoolName} disabled={isAddToExisting} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., PrimaryUsers" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.userPoolName && touched.userPoolName ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="userPoolName" /></div>
                                            </Grid>
                                        )}

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Token Header Source" />
                                            <TextField variant="filled" fullWidth name="tokenHeaderSource" value={values.tokenHeaderSource} disabled={isAddToExisting} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., Authorization or x-api-key" hiddenLabel
                                                sx={{ "& .MuiFilledInput-root": { border: errors.tokenHeaderSource && touched.tokenHeaderSource ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="tokenHeaderSource" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', gap: 1, mb: 0 }}>
                                                <CompulsoryFieldsLabel fieldName="Consumer Group" />
                                                <Tooltip
                                                    title={
                                                        <Typography sx={{ whiteSpace: 'pre-line', color: '#ffffff', fontFamily: "Gilroy-Regular", fontSize: "14px" }}>
                                                            {`Free Tier
                                                                    \u00A0\u00A0Throttling Rate: 10
                                                                    \u00A0\u00A0Burst: 20
                                                                    \u00A0\u00A0Quota: 5000

                                                                    Premium Partner Plan
                                                                    \u00A0\u00A0Throttling Rate: 500
                                                                    \u00A0\u00A0Burst: 1000
                                                                    \u00A0\u00A0Quota: 100000`}
                                                        </Typography>
                                                    }
                                                    slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                                >
                                                    <Avatar
                                                        src="/assets/info.svg"
                                                        alt="info"
                                                        sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                            <Autocomplete 
                                                        options={consumerGroupList.map(group => group.name)}
                                                        value={values.consumerGroup} 
                                                        disabled={isAddToExisting}
                                                        onChange={(_, newValue) => setFieldValue("consumerGroup", newValue || "")}
                                                        renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select a Consumer Group" hiddenLabel
                                                            sx={{ "& .MuiFilledInput-root": { border: errors.consumerGroup && touched.consumerGroup ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                            InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                                    />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="consumerGroup" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="APIs Backend" />
                                            <Autocomplete
                                                multiple
                                                options={apiBackendList?.map((api) => api.name)}
                                                filterSelectedOptions
                                                ChipProps={{ size: "small" }}
                                                value={values.apis}
                                                onChange={(e, newVal) => {
                                                    setFieldValue("apis", newVal);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="filled"
                                                        fullWidth
                                                        placeholder="Select one or more APIs Backend"
                                                        hiddenLabel
                                                        sx={{ "& .MuiFilledInput-root": { border: getIn(errors, 'apis') && getIn(touched, 'apis') ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            style: { borderRadius: "8px" },
                                                            disableUnderline: true,
                                                        }}
                                                    />
                                                )}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="apis" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <CompulsoryFieldsLabel fieldName="Categories" />
                                            <Autocomplete options={categoryList} value={values.categories} disabled={isAddToExisting}
                                                onChange={(_, newValue) => setFieldValue("categories", newValue || "")}
                                                renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select a Category" hiddenLabel
                                                    sx={{ "& .MuiFilledInput-root": { border: errors.categories && touched.categories ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                    InputProps={{ ...params.InputProps, disableUnderline: true, style: { borderRadius: "8px" } }} />)}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="categories" /></div>
                                        </Grid>

                                        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.wafEnabled}
                                                        disabled={isAddToExisting}
                                                        onChange={(e) => setFieldValue("wafEnabled", e.target.checked)}
                                                        name="wafEnabled"
                                                        sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
                                                    />
                                                }
                                                label="AWS WAF Enabled"
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="wafEnabled" /></div>

                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.addBotControl}
                                                        disabled={isAddToExisting}
                                                        onChange={(e) => setFieldValue("addBotControl", e.target.checked)}
                                                        name="addBotControl"
                                                        sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
                                                    />
                                                }
                                                label="Add Bot Control"
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="addBotControl" /></div>
                                        </Grid>
                                    </Grid>
                                </div>

                                <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                    <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>
                                        API Tags / Labels
                                    </Typography>
                                    <Divider sx={{ marginBottom: "24px" }} />
                                    <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Value</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth disabled value="cost-center"  variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth placeholder="Enter value" value={values.costCenterValue} disabled={isAddToExisting} hiddenLabel variant="filled" name="costCenterValue"
                                                sx={{ "& .MuiFilledInput-root": { border: errors.costCenterValue && touched.costCenterValue ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                onChange={handleChange} onBlur={handleBlur}
                                                InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}
                                            />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" component="div" /></div>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={4} mt="8px">
                                        {values.costLabelPairs?.map((pair: any, index: any) => (
                                            <React.Fragment key={pair.id || index}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth placeholder="Enter key" value={pair.cost} disabled={isAddToExisting} hiddenLabel variant="filled"
                                                        sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                        onChange={(event) => setFieldValue(`costLabelPairs[${index}].cost`, event.target.value)}
                                                        InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].cost`} component="div" /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box display="flex" alignItems="center">
                                                        <TextField fullWidth placeholder="Enter value" value={pair.label} disabled={isAddToExisting} hiddenLabel variant="filled"
                                                            sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }}
                                                            onChange={(event) => setFieldValue(`costLabelPairs[${index}].label`, event.target.value)}
                                                            InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                        <IconButton sx={{ marginLeft: "8px" }} onClick={() => {
                                                            const newPairs = values.costLabelPairs.filter((_: any, i: any) => i !== index);
                                                            setFieldValue("costLabelPairs", newPairs);
                                                        }}>
                                                            <img alt="close-icon" src="/assets/close-icon.svg" />
                                                        </IconButton>
                                                    </Box>
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].label`} component="div" /></div>
                                                </Grid>
                                            </React.Fragment>
                                        ))}
                                    </Grid>
                                    <Button variant="text" sx={{ color: "#000000", textTransform: "none", mt: "16px" }} onClick={() => setFieldValue("costLabelPairs", [...values.costLabelPairs, { cost: "", label: "", id: crypto.randomUUID() }])}>
                                        + Add Key Value Pair
                                    </Button>
                                </div>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/apis-gateway")}>Cancel</Button>
                                    <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black" }, textTransform: "none", px: 4, }} variant="contained" size="large" type="submit" disabled={!isValid || !dirty || isViewer}>
                                        {isAddToExisting ? "Update Gateway": isEdit ? "Save Changes" : "Create Gateway"}
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                    )}
                </Grid>
                <Grid item xs={1}></Grid>
            </Grid>
        </Container>
    );
};

// --- Main Export Component ---
const APIGatewayForm: React.FC<APIGatewayFormProps> = ({ isEdit, isAddToExisting }) => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"apis-gateway"} />
            <Box component="main" sx={{ flexGrow: 1 }}>
                <APIGatewayFormContent isEdit={isEdit} isAddToExisting={isAddToExisting} />
            </Box>
        </Box>
    );
};

export default APIGatewayForm;